import React, { useContext } from 'react';
import { PostSource, QueueFilterPostStatus } from '../../../../models/graphql';
import { EmptyPlaceholder } from '../../../../components/containers/EmptyPlaceholder';
import emptyQueue from '../../../../assets/images/emptyQueue.svg';
import noErrors from '../../../../assets/images/noErrors.svg';
import emptyPostLibrary from '../../../../assets/images/emptyPostLibrary.svg';
import nothingFound from '../../../../assets/images/nothingFound.svg';
import { IconsEnum } from '../../../../constants/icons';
import { PostScheduleViewContext } from '../PostSchedule.controller';
import { Link, useNavigate } from 'react-router-dom';
import { usePauseSettings } from '../../../../hooks/usePauseSettings';
import publishingPaused from '../../../../assets/images/publishingPaused.svg';
import { TopNavContext } from '../../../../providers/TopNavProvider';
import { BeaconContext } from '../../../../providers/BeaconProvider';
import { useOpenPostEditor } from '../../../../hooks/useOpenPostEditor';

export type PostScheduleEmptyHeroBoxViewProps = {
  status: QueueFilterPostStatus;
  categoryIdFilter: string | undefined;
  socialProfileIdFilter: string | undefined;
  context: PostScheduleViewContext;
};

export const PostScheduleEmptyHeroBoxView = (props: PostScheduleEmptyHeroBoxViewProps): JSX.Element => {
  const { beacon } = useContext(BeaconContext);
  const { state } = useContext(TopNavContext);
  const { openNewPost } = useOpenPostEditor();
  const navigate = useNavigate();
  const {
    workspacePublishingPaused,
    isCategorySeasonallyPaused,
    isCategoryPausedForAllSocialProfiles,
    isSocialProfilePausedForAllCategories,
    isSocialProfilePausedForCategory
  } = usePauseSettings();

  const NoScheduledPosts = (): JSX.Element => {
    if (workspacePublishingPaused) {
      return (
        <EmptyPlaceholder
          title={'All publishing is paused'}
          image={publishingPaused}
          buttonTitle={'Go to Pause Settings'}
          buttonAction={() => navigate({ pathname: '/settings/pause' })}>
          <div className={'padding-bottom-small-16'}>
            All Queued Posts will be remain dormant, but your manually-scheduled Posts will fail to publish. You can
            also go for more granular <Link to={'/settings/pause'}>pause settings</Link>.
          </div>
        </EmptyPlaceholder>
      );
    }

    const filterCategoryId = props.categoryIdFilter;
    const filterSocialProfileId = props.socialProfileIdFilter;

    // Category filter selected but no Profile filter.
    if (filterCategoryId && !filterSocialProfileId) {
      if (isCategorySeasonallyPaused(filterCategoryId)) return categorySeasonalPaused;
      if (isCategoryPausedForAllSocialProfiles(filterCategoryId)) return categoryPaused;
    }

    // Profile filter selected but no Category filter.
    if (!filterCategoryId && filterSocialProfileId) {
      if (isSocialProfilePausedForAllCategories(filterSocialProfileId)) return socialProfilePaused;
    }

    // Both Category filter & Profile filter are selected.
    if (filterCategoryId && filterSocialProfileId) {
      if (isCategorySeasonallyPaused(filterCategoryId)) return categorySeasonalPaused;
      if (isSocialProfilePausedForCategory(filterSocialProfileId, filterCategoryId)) {
        return socialProfilePausedForCategory;
      }
    }

    return (
      <EmptyPlaceholder
        title={'You don’t have any Posts scheduled'}
        image={emptyQueue}
        buttonTitle={'Add a Post'}
        buttonIcon={IconsEnum.plus}
        buttonAction={() => openNewPost({ source: PostSource.QUEUE, onClose: state.newPostCloseCallback })}>
        <div>
          Your Queue is where all your scheduled content lives, ready to be published automatically — saving you time
          and hassle. Add your own posts with the Post Editor or{' '}
          <Link to={'/discover/importer'}>from a CSV spreadsheet</Link>; or{' '}
          <Link to={'/discover/find'}>import content from RSS feeds, social media</Link>, or our{' '}
          <Link to={'/discover/extension'}>Chrome Extension</Link>.
          <p>
            <a onClick={() => beacon('article', '590', { type: 'modal' })}>
              Learn more
            </a>
          </p>
        </div>
      </EmptyPlaceholder>
    );
  };

  const noFailedPosts = (
    <EmptyPlaceholder title={'Nothing to Fix – Just Smooth Posting!'} image={noErrors}>
      <div className={'padding-bottom-small-16'}>
        If a post ever fails to publish, this is where you’ll find details of what happened, along with troubleshooting
        steps to get it back on track.
        <br />
        But right now, everything’s working perfectly!
      </div>
    </EmptyPlaceholder>
  );

  const noPastPosts = (
    <EmptyPlaceholder
      title={'You don’t have any published posts yet...'}
      image={emptyPostLibrary}
      buttonTitle={'Add a Post'}
      buttonIcon={IconsEnum.plus}
      buttonAction={() => openNewPost({ source: PostSource.QUEUE, onClose: state.newPostCloseCallback })}>
      <div className={'padding-bottom-small-16'}>
        Your published posts will appear here, along with engagement metrics to help you track performance. Start
        posting to unlock valuable insights and optimize your strategy!
      </div>
    </EmptyPlaceholder>
  );

  const noDeletedPosts = (
    <EmptyPlaceholder title={'You don’t have any trashed posts'} image={nothingFound}>
      <div className={'padding-bottom-small-16'}>
        Trashed posts stay here for 30 days before being permanently removed.
        <br />
        You can restore them anytime before then.
      </div>
    </EmptyPlaceholder>
  );

  const noUnQueuedPosts = (
    <EmptyPlaceholder title={'You don’t have any unqueued posts'} image={nothingFound}>
      <div className={'padding-bottom-small-16'}>
        <p>
          Unqueued posts don’t have a matching Timeslot in your Posting Plan, so SmarterQueue doesn’t know when to
          publish them. To fix this, add a Timeslot for the relevant Profile and Category in your Posting Plan.
        </p>
        <p>
          <a onClick={() => beacon('article', '697', { type: 'modal' })}>
            Learn more
          </a>
        </p>
      </div>
    </EmptyPlaceholder>
  );

  const categorySeasonalPaused = (
    <EmptyPlaceholder title={'This Category is Seasonally-Paused'} image={emptyPostLibrary}>
      <div className={'padding-bottom-small-16'}>
        <p>
          Posts in this Category won’t be published until its seasonal dates are active again. You can find them in the
          Paused filter in the meantime.
        </p>
        <p>
          Edit the seasonal dates for your{' '}
          <a href={'/publish/categories'} target={'_blank'} rel="noreferrer">
            Category
          </a>{' '}
          to resume posting.
        </p>
      </div>
    </EmptyPlaceholder>
  );

  const categoryPaused = (
    <EmptyPlaceholder title={'This Category is paused for all Profiles'} image={emptyPostLibrary}>
      <div className={'padding-bottom-small-16'}>
        <p>Posts in this Category won't be published, and can be found in the Paused filter.</p>
        <p>
          Edit your{' '}
          <a href={'/settings/pause'} target={'_blank'} rel="noreferrer">
            Pause Settings
          </a>{' '}
          to resume posting.
        </p>
      </div>
    </EmptyPlaceholder>
  );

  const socialProfilePaused = (
    <EmptyPlaceholder title={'This Profile is paused for all Categories'} image={emptyPostLibrary}>
      <div className={'padding-bottom-small-16'}>
        <p>Posts in this Profile won't be published, and can be found in the Paused filter.</p>
        <p>
          Edit your{' '}
          <a href={'/settings/pause'} target={'_blank'} rel="noreferrer">
            Pause Settings
          </a>{' '}
          to resume posting.
        </p>
      </div>
    </EmptyPlaceholder>
  );

  const socialProfilePausedForCategory = (
    <EmptyPlaceholder title={'This Profile is paused for this Category'} image={emptyPostLibrary}>
      <div className={'padding-bottom-small-16'}>
        <p>Posts in this Profile and Category won't be published, and can be found in the Paused filter.</p>
        <p>
          Edit your{' '}
          <a href={'/settings/pause'} target={'_blank'} rel="noreferrer">
            Pause Settings
          </a>{' '}
          to resume posting.
        </p>
      </div>
    </EmptyPlaceholder>
  );

  const noPausedPosts = (
    <EmptyPlaceholder title={'You don’t have any paused posts right now'} image={emptyPostLibrary}>
      <div className={'padding-bottom-small-16'}>
        <p>
          Pausing lets you temporarily stop posts from being published without deleting them. You can pause your entire
          Queue, specific Profiles, or individual Categories — perfect for holding off on content until you're ready, or
          for quickly stopping posts in response to unexpected events.
        </p>
        <p>
          Need to make changes? Adjust your{' '}
          <a href={'/settings/pause'} target={'_blank'} rel="noreferrer">
            Pause Settings
          </a>{' '}
          anytime.
        </p>
      </div>
    </EmptyPlaceholder>
  );

  const noDraftPosts = (
    <EmptyPlaceholder title={'You don’t have any drafts Posts right now'} image={emptyPostLibrary}>
      <div className={'padding-bottom-small-16'}>
        <p>
          Drafts let you save posts as a work in progress, so you can refine your content before publishing. Use drafts
          to plan ahead, collaborate with your team, or store ideas until you're ready to schedule. Once a draft is
          complete, you can easily schedule or publish it with a click.
        </p>
      </div>
    </EmptyPlaceholder>
  );

  switch (props.status) {
    case QueueFilterPostStatus.SCHEDULED:
      return <NoScheduledPosts />;
    case QueueFilterPostStatus.PAST:
      return noPastPosts;
    case QueueFilterPostStatus.FAILED:
      return noFailedPosts;
    case QueueFilterPostStatus.PAUSED:
      return noPausedPosts;
    case QueueFilterPostStatus.DRAFT:
      return noDraftPosts;
    case QueueFilterPostStatus.DELETED:
      return noDeletedPosts;
    case QueueFilterPostStatus.UNQUEUED:
      return noUnQueuedPosts;
    default:
      return <NoScheduledPosts />;
  }
};
